import React, { useState } from "react";
import { DialogTitle, Description } from "@headlessui/react";
import { auth } from "../firebase";
import toast from "react-hot-toast";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ModalDialog from "./ModalDialog";

// In dev (client on 3002), hit server directly to avoid proxy issues
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.port === "3002"
    ? "http://localhost:5001"
    : "");

function SuggestTopicDialog({ isOpen, onClose }) {
  const [idea, setIdea] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!idea.trim()) {
      toast.error("Please enter your topic idea.");
      return;
    }

    const userEmail = auth.currentUser?.email || "Unknown (not signed in)";

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/suggest-topic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idea: idea.trim(), userEmail }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Request failed: ${response.status}`);
      }

      toast.success("Thanks! Your suggestion has been sent.");
      setIdea("");
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to send suggestion. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  return (
    <ModalDialog
      open={isOpen}
      onClose={handleClose}
      panelClassName="max-w-md w-full p-6"
    >
      <div className="flex justify-between items-start gap-4 mb-4">
        <DialogTitle
          as="h2"
          className="text-xl font-semibold text-gray-900 pr-2"
        >
          Suggest a Lesson Topic
        </DialogTitle>
        <button
          type="button"
          onClick={handleClose}
          disabled={submitting}
          className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-target"
          aria-label="Close dialog"
        >
          <XMarkIcon className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <Description className="text-gray-600 mb-4 block">
          Share your topic idea
        </Description>
        <label htmlFor="suggest-topic-idea" className="sr-only">
          Topic idea
        </label>
        <textarea
          id="suggest-topic-idea"
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          placeholder="What topic would you like to see a lesson or video about?"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          rows={4}
          disabled={submitting}
          autoFocus
        />
        <div className="flex justify-end gap-3 mt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            {submitting ? "Sending..." : "Submit"}
          </button>
        </div>
      </form>
    </ModalDialog>
  );
}

export default SuggestTopicDialog;
